import { get_token } from "@/router";

export const users_url = process.env.VUE_APP_VIEW_USER_URL || "";
export const users_list_url = process.env.VUE_APP_USER_LIST_URL || "";
export const create_user_url = process.env.VUE_APP_CREATE_USER_URL || "";
export const roles_url = process.env.VUE_APP_ROLES_URL || "";
export const client_app = process.env.VUE_APP_CLIENT_APP;

const backend_error_messages = {
    invalid_credentials: "Those login details did not match our records. Please check your email and password.",
    user_not_found: "We could not find that user. Refresh the page and try again.",
    profile_not_found: "We could not find that user profile. Refresh the page and try again.",
    invalid_or_expired_token: "That reset link is invalid or has expired. Please request a new one.",
    user_inactive: "This user account is inactive.",
    internal_api_key_not_configured: "The server is not fully configured for this request.",
    invalid_internal_auth: "This request is not authorized.",
    invalid_timestamp: "This request could not be verified.",
    stale_request: "This request has expired. Please try again.",
    invalid_signature: "This request could not be verified.",
    sso_not_configured: "Single sign-on is not fully configured.",
    expired_sso_assertion: "Your sign-in link has expired. Please sign in again.",
    invalid_sso_assertion: "Your sign-in link could not be verified.",
    invalid_sso_payload: "The sign-in request was not valid.",
    invalid_dashboard_platform: "This dashboard platform is not available for your account.",
    duplicate_external_user_id: "This external user is linked more than once. Please contact support.",
    reset_link_sent: "If that email exists, a reset link has been sent.",
    password_reset_successful: "Your password has been reset successfully.",
    user_deactivated: "User deactivated.",
    logout_successful: "You have been signed out.",
    "challenge token required": "Your verification session expired. Please sign in again.",
    "invalid or expired OTP": "That verification code is invalid or expired. Please request a new one."
};

export function get_auth_headers(include_json_content_type = true) {
    const auth_token = get_token();
    const headers = {
        "Accept": "application/json"
    };

    if (include_json_content_type) {
        headers["Content-Type"] = "application/json";
    }

    if (auth_token) {
        headers.Authorization = `Token ${auth_token}`;
    }

    return headers;
}

export function get_error_data(data) {
    return data?.errors || data?.detail || data?.error || data?.message || data;
}

function humanize_field_name(field) {
    return String(field || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function get_message_text(messages) {
    if (Array.isArray(messages)) {
        return messages.map((message) => get_message_text(message)).filter(Boolean).join(", ");
    }

    if (messages && typeof messages === "object") {
        return Object.entries(messages)
            .map(([field, field_messages]) => `${humanize_field_name(field)}: ${get_message_text(field_messages)}`)
            .join(" ");
    }

    return String(messages || "").trim();
}

function get_normalized_error_message(message) {
    const raw_message = get_message_text(message);

    return ({
        raw_message,
        normalized_message: raw_message.toLowerCase()
    });
}

const general_error_messages = {
    "failed to fetch": "We could not reach the server. Please check your connection and try again.",
    networkerror: "We could not reach the server. Please check your connection and try again.",
    unauthorized: "Your session needs a quick refresh. Please sign in again, then try once more.",
    forbidden: "Your session needs a quick refresh. Please sign in again, then try once more.",
    "not configured": "This user-management action is not fully set up yet. We are showing the information that is available."
};

function get_mapped_error_message(error_context) {
    return backend_error_messages[String(error_context.raw_message)]
        || backend_error_messages[String(error_context.normalized_message)]
        || general_error_messages[String(error_context.normalized_message)]
        || "";
}

export function get_user_friendly_error_message(message, fallback_message = "We could not complete that request. Please try again.") {
    const error_context = get_normalized_error_message(message);

    return get_mapped_error_message(error_context) || error_context.raw_message || fallback_message;
}

export function get_error_message(data, fallback_message = "Unable to complete user request.") {
    const error_data = get_error_data(data);

    if (!error_data) {
        return fallback_message;
    }

    if (typeof error_data === "string") {
        return get_user_friendly_error_message(error_data, fallback_message);
    }

    if (Array.isArray(error_data)) {
        return get_user_friendly_error_message(error_data, fallback_message);
    }

    if (typeof error_data === "object") {
        const field_messages = Object.entries(error_data)
            .map(([field, messages]) => `${humanize_field_name(field)}: ${get_message_text(messages)}`)
            .join(" ");

        return get_user_friendly_error_message(field_messages, fallback_message);
    }

    return fallback_message;
}

export async function get_response_data(response, fallback_message) {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(get_error_message(data, fallback_message));
    }

    return data;
}

export function get_fetch_error_message(error, fallback_message = "Unable to reach the server.") {
    if (error?.message && error.message !== "Failed to fetch") {
        return get_user_friendly_error_message(error.message, fallback_message);
    }

    return get_user_friendly_error_message(error?.message, fallback_message);
}

export function require_array_response(response_data, fallback_message) {
    if (Array.isArray(response_data)) {
        return response_data;
    }

    throw new Error(fallback_message);
}
